// app/frontend/components/TabLayout/TabLayout.tsx
import React, { useState, useRef } from 'react';
import { Box, Tabs, Tooltip } from '@mantine/core';
import { useScrollableTabs } from '../../../src/hooks/useScrollableTabs';

export interface Tab {
  id: string;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: string | number;
  tooltip?: string;
}

export interface TabLayoutProps {
  tabs: Tab[];
  content: Record<string, React.ReactNode>;
  defaultTab?: string;
  onTabChange?: (tabId: string) => void;
  variant?: 'default' | 'pills' | 'outline';
  color?: string;
  orientation?: 'horizontal' | 'vertical';
  className?: string;
  tabsListProps?: any;
  containerProps?: any;
}

const TabLayout: React.FC<TabLayoutProps> = ({
  tabs,
  content,
  defaultTab,
  onTabChange,
  variant = 'default',
  color = 'orange',
  orientation = 'horizontal',
  className,
  tabsListProps,
  containerProps,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const { tabListReference } = useScrollableTabs();

  const handleTabChange = (tabId: string | null) => {
    if (!tabId) return;

    setActiveTab(tabId);
    onTabChange?.(tabId);
  };

  const renderTab = (tab: Tab) => {
    const tabElement = (
      <Tabs.Tab
        key={tab.id}
        value={tab.id}
        leftSection={tab.icon}
        disabled={tab.disabled}
        rightSection={
          tab.badge ? (
            <Box
              component="span"
              style={{
                backgroundColor: color === 'orange' ? '#EA580C' : color,
                color: 'white',
                borderRadius: '10px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: 600,
                minWidth: '18px',
                textAlign: 'center',
              }}
            >
              {tab.badge}
            </Box>
          ) : undefined
        }
        style={{
          color: 'white',
          borderBottom:
            activeTab === tab.id ? `2px solid ${color === 'orange' ? '#EA580C' : color}` : '2px solid transparent',
          backgroundColor: activeTab === tab.id ? (color === 'orange' ? '#EA580C' : color) : 'transparent',
          minWidth: '150px',
          fontWeight: 500,
          borderRadius: '4px 4px 0px 0px',
        }}
        size="sm"
      >
        {tab.label}
      </Tabs.Tab>
    );

    // Wrap in tooltip if tooltip is provided
    if (tab.tooltip) {
      return (
        <Tooltip key={tab.id} label={tab.tooltip} position="top" withArrow>
          {tabElement}
        </Tooltip>
      );
    }

    return tabElement;
  };

  return (
    <Box className={className} {...containerProps}>
      <Tabs value={activeTab} onChange={handleTabChange} color={color} variant={variant} orientation={orientation}>
        <Tabs.List ref={tabListReference} {...tabsListProps}>
          {tabs.map(renderTab)}
        </Tabs.List>

        {/* Render the content for the active tab */}
        <Box mt="lg">{content[activeTab] || null}</Box>
      </Tabs>
    </Box>
  );
};

export default TabLayout;
